import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Modal,
  Button,
  useTheme,
  alpha,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useJsApiLoader } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

// Constants
import { API_KEY, STEPS } from "./constants.js";

// Custom Hooks
import { useAuth } from "../../contexts/AuthContext.js";
import { useDivesitesContext } from "../../hooks/useDivesitesContext.js";
import useDivesiteForm from "./hooks/useDivesiteForm.js";
import useSnackbar from "./hooks/useSnackbar.js";
import useSubmission from "./hooks/useSubmission.js";
import useTurnstile from "./hooks/useTurnstile.js";

// Components
import StepperNavigation from "./StepperNavigation.js";
import BasicInfoStep from "./steps/BasicInfoStep.js";
import LocationStep from "./steps/LocationStep.js";
import DiveDetailsStep from "./steps/DiveDetailsStep.js";
import AdditionalInfoStep from "./steps/AdditionalInfoStep.js";

// Glassmorphic Form Container
const GlassFormContainer = styled(Box)(({ theme }) => ({
  maxWidth: "700px",
  margin: "0 auto",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.9
  )} 0%, ${alpha(theme.palette.background.paper, 0.75)} 100%)`,
  backdropFilter: "blur(25px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 20,
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.4
    )}, transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${alpha(
      theme.palette.primary.main,
      0.03
    )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
      theme.palette.secondary.main,
      0.03
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Glassmorphic Header Typography
const GlassHeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "0.9rem",
  color: alpha(theme.palette.text.primary, 0.8),
  textAlign: "center",
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1.5, 2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.info.main,
    0.08
  )} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
  borderRadius: 12,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.info.main,
      0.3
    )}, transparent)`,
  },
}));

// Glassmorphic Stepper
const GlassStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.06
  )} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 16,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.primary.main,
      0.2
    )}, transparent)`,
  },

  "& .MuiStepLabel-root": {
    "& .MuiStepLabel-label": {
      fontWeight: 500,
      fontSize: "0.9rem",
      "&.Mui-active": {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
      "&.Mui-completed": {
        color: theme.palette.success.main,
        fontWeight: 500,
      },
    },
  },

  "& .MuiStepIcon-root": {
    "&.Mui-active": {
      color: theme.palette.primary.main,
      filter: `drop-shadow(0 2px 8px ${alpha(
        theme.palette.primary.main,
        0.3
      )})`,
    },
    "&.Mui-completed": {
      color: theme.palette.success.main,
      filter: `drop-shadow(0 2px 8px ${alpha(
        theme.palette.success.main,
        0.3
      )})`,
    },
  },
}));

// Glassmorphic Progress Bar
const GlassProgressBar = styled(LinearProgress)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 8,
  height: 6,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.6
  )} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  overflow: "hidden",

  "& .MuiLinearProgress-bar": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: 8,
  },
}));

// Glassmorphic Step Content Container
const GlassStepContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.4
  )} 0%, ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(12px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: 16,
  marginBottom: theme.spacing(2),
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.common.white,
      0.2
    )}, transparent)`,
  },
}));

// Glassmorphic Modal
const GlassModalContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.95
  )} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`,
  backdropFilter: "blur(30px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  borderRadius: 20,
  padding: theme.spacing(4),
  textAlign: "center",
  boxShadow: `0 25px 80px ${alpha(theme.palette.common.black, 0.3)}`,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${alpha(
      theme.palette.success.main,
      0.5
    )}, transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 50% 20%, ${alpha(
      theme.palette.success.main,
      0.05
    )} 0%, transparent 50%)`,
    pointerEvents: "none",
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Glassmorphic Modal Button
const GlassModalButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.success.main,
    0.15
  )} 0%, ${alpha(theme.palette.success.main, 0.08)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
  borderRadius: 12,
  color: theme.palette.success.main,
  fontWeight: 600,
  textTransform: "none",
  padding: theme.spacing(1, 3),
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.common.white,
      0.1
    )}, transparent)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.success.main,
      0.2
    )} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
    transform: "translateY(-2px)",
    boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.25)}`,

    "&::before": {
      opacity: 1,
    },
  },
}));

// Enhanced Modal with Glassmorphic Backdrop
const GlassModal = styled(Modal)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: alpha(theme.palette.common.black, 0.6),
    backdropFilter: "blur(8px)",
  },
}));


const DivesiteForm = ({ isStubMode = false, onStubSubmit, stubModeText }) => {
  console.log("DivesiteForm rendered");
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: API_KEY });
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const { userObjectId } = useAuth();
  const { dispatch } = useDivesitesContext();

  const {
    formState,
    handleInputChange,
    handleMarkerDragEnd,
    handleImageChange,
    handleImageDescriptionChange,
    handleMediaImagesChange,
    handleMediaImageDescriptionChange,
    resetForm,
    setFormState,
    handleGetLocation: handleGetLocationFromHook,
    isLocating,
    setGeolocationAttempted,
  } = useDivesiteForm(isLoaded, showSnackbar);

  const {
    turnstileToken,
    setTurnstileToken,
    loadTurnstileScript,
    TURNSTILE_SITE_KEY,
  } = useTurnstile(showSnackbar, theme.palette.mode);

  const {
    isSubmitting,
    modalOpen,
    submissionSuccessful,
    setModalOpen,
    handleSubmit: submitForm,
  } = useSubmission(
    formState,
    userObjectId,
    dispatch,
    showSnackbar,
    resetForm,
    setTurnstileToken,
    turnstileToken
  );

  const handleGetLocation = () => {
    setGeolocationAttempted(true);
    handleGetLocationFromHook(
      () => {},
      (error) => {
        const messages = {
          1: "Location access denied.",
          2: "Unable to determine your location.",
          3: "Location timeout.",
        };
        showSnackbar(messages[error.code] || "An unknown error occurred.");
      }
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    navigate("/sites");
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoStep
            formState={formState}
            handleInputChange={handleInputChange}
          />
        );
      case 1:
        return (
          <LocationStep
            isLoaded={isLoaded}
            formState={formState}
            handleInputChange={handleInputChange}
            handleMarkerDragEnd={handleMarkerDragEnd}
            onGetLocation={handleGetLocation}
            isLocating={isLocating}
          />
        );
      case 2:
        return (
          <DiveDetailsStep
            formState={formState}
            setFormState={setFormState}
            handleInputChange={handleInputChange}
          />
        );
      case 3:
        return (
          <AdditionalInfoStep
            formState={formState}
            setFormState={setFormState}
            handleImageChange={handleImageChange}
            handleImageDescriptionChange={handleImageDescriptionChange}
            handleMediaImagesChange={handleMediaImagesChange}
            handleMediaImageDescriptionChange={
              handleMediaImageDescriptionChange
            }
            isSubmitting={isSubmitting}
            turnstileSiteKey={TURNSTILE_SITE_KEY}
            loadTurnstileScript={loadTurnstileScript}
            turnstileToken={turnstileToken}
          />
        );
      default:
        return "Unknown step";
    }
  };

  const handleSetActiveStep = (newStep) => {
    setActiveStep(newStep);
  };

  useEffect(() => {
    if (activeStep === 3) {
      loadTurnstileScript();
    }
  }, [activeStep, loadTurnstileScript]);

  return (
    <GlassFormContainer
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
    >
    {isStubMode && stubModeText && (
      <Alert severity="info" sx={{ mb: 3 }}>
        {stubModeText}
      </Alert>
    )}
    <GlassHeaderText>
      Please log a dive after you create it if you dove this site.
    </GlassHeaderText>

    <GlassStepper activeStep={activeStep}>
      {STEPS.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </GlassStepper>

    {isSubmitting && <GlassProgressBar />}

    <GlassStepContent>{getStepContent(activeStep)}</GlassStepContent>

    <StepperNavigation
      activeStep={activeStep}
      setActiveStep={handleSetActiveStep}
      isSubmitting={isSubmitting}
      handleSubmit={submitForm}
      totalSteps={STEPS.length}
      turnstileToken={turnstileToken}
      isStubMode={isStubMode}
    />

    <SnackbarComponent />

    <GlassModal
      open={modalOpen}
      onClose={handleCloseModal}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
    <GlassModalContent>
          <Typography
            id="modal-title"
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Thank you for your submission!
          </Typography>
          <Typography
            id="modal-description"
            sx={{
              mb: 3,
              color: alpha(theme.palette.text.primary, 0.8),
              fontSize: "1rem",
              lineHeight: 1.6,
            }}
          >
            Our editors will review your submission and publish it soon.
          </Typography>
          <GlassModalButton
            onClick={handleCloseModal}
            variant="contained"
            size="large"
          >
            Close
          </GlassModalButton>
        </GlassModalContent>
      </GlassModal>
      <GlassModal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <GlassModalContent>
          <Typography
            id="modal-title"
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
      {isStubMode 
        ? "Thank you for your submission!" 
        : "Divesite Created Successfully!"}
    </Typography>
    <Typography id="modal-modal-description" sx={{ mt: 2, mb: 2 }}>
      {isStubMode 
        ? "Our editors will review your submission before publishing it to the main collection."
        : "Your divesite has been added to our database."}
    </Typography>
          <GlassModalButton
            onClick={handleCloseModal}
            variant="contained"
            size="large"
          >
            Close
          </GlassModalButton>
        </GlassModalContent>
      </GlassModal>
    </GlassFormContainer>
  );
};

export default DivesiteForm;
